import { format } from 'date-fns'

type BooleanString = 'true' | 'false'

interface PhaseItemFilters {
  active?: BooleanString
  current?: BooleanString
  overdue?: BooleanString
  pending?: BooleanString
  to_user_id: string
  type: 'approval'
  undated_created_days?: string
}

// Mirrors UNDATED_CREATED_DAYS in the Assignments view: undated work is
// hidden once it is this stale, in every bucket that can show undated work.
const UNDATED_CREATED_DAYS = '30'

const getPhaseItemFiltersByApprovalType = (
  type: string
): Partial<PhaseItemFilters> => {
  switch (type) {
    case 'completed':
      return {
        pending: 'false',
      }
    case 'overdue':
      return {
        current: 'true',
        overdue: 'true',
        pending: 'true',
      }
    case 'pending':
      return {
        overdue: 'false',
        pending: 'true',
        undated_created_days: UNDATED_CREATED_DAYS,
      }
    default:
      return {}
  }
}

interface PhaseItemParams {
  fields: {
    content: string
    moment: string
    phase: string
    service: string
  }
  filter: PhaseItemFilters
  include: string[]
  expose?: { [key: string]: string }
}

export const getPhaseItemParamsForApprovalsByType = (
  type: string,
  user_id: string,
  brandId: string | null
): PhaseItemParams => ({
  filter: {
    ...getPhaseItemFiltersByApprovalType(type),
    to_user_id: user_id,
    type: 'approval',
    ...(brandId && { brand_id: brandId }),
  },
  include: ['approvable', 'approvable.service', 'phase'],
  fields: {
    content: ['name', 'moment', 'service'].join(','),
    moment: ['scheduled_at', 'posts'].join(','),
    phase: ['due_date'].join(','),
    service: ['color', 'system_name'].join(','),
  },
  expose: {
    unscheduled_posts: 'true',
  },
})

interface AssignmentFilters {
  assignment_type: 'approval'
  assigned_user_id: string
  due_date?: {
    eq?: string
    gt?: string
    gteq?: string
    lt?: string
    lteq?: string
  }
  status?: 'complete' | 'incomplete'
  stage_status?: 'active' | 'complete' | 'inactive'
}

const getAssignmentFiltersByApprovalType = (
  type: string
): Partial<AssignmentFilters> => {
  switch (type) {
    case 'completed':
      return {
        status: 'complete',
      }
    case 'overdue':
      return {
        due_date: {
          lt: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
        },
        status: 'incomplete',
        stage_status: 'active',
      }
    case 'pending':
      return {
        due_date: {
          gteq: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
        },
        status: 'incomplete',
        stage_status: 'active',
      }
    default:
      return {}
  }
}

interface AssignmentParams {
  filter: AssignmentFilters
  include: string
  page: {
    limit: number
  }
}

export const getAssignmentsParamsForApprovalsByType = (
  type: string,
  user_id: string,
  brandId: string | null
): AssignmentParams => ({
  include: [
    'stage',
    'stage.workflow',
    'stage.workflow.context',
    'responses',
  ].join(','),
  filter: {
    assignment_type: 'approval',
    assigned_user_id: user_id,
    ...(brandId && { brand_id: brandId }),
    ...getAssignmentFiltersByApprovalType(type),
  },
  page: {
    limit: 800,
  },
})
